/**
 * Core-100 R3 — "Needs attention": the records Round 2 built and proved but nobody could see.
 *
 *  · Unexpected arrivals (CORE-AP, `unexpected_arrivals` / `decide_arrival`): a camera saw a vehicle arrive that
 *    matched no open visit. The front office says what it was; deciding moves nothing (location stays a
 *    suggestion until a person places the vehicle).
 *  · Timers open too long (CORE-AJ, `timer_session_flags`): a notice — the technician decides; OptiBay never stops
 *    a timer on anyone's behalf.
 *  · Running long (CORE-BF, `ticket_run_long_episodes`): stated only as far as the record supports the cause.
 *    R4: with its CONSEQUENCES (`run_long_consequences`): the later work in the lane that can no longer start on
 *    time, the promises in that lane already in trouble, and whether it now runs past the technician's day.
 *  · Promises in trouble (CORE-BH, `due_out_risk_episodes`, R4): missed and at risk are said apart — the actions
 *    differ — ordered by severity, then a waiting customer, then the promised time (OD-07). The options
 *    themselves are on the ticket (the board's resolution options); nothing here moves anything.
 *    R4 W5 (CORE-BH GAP-006): when an OPEN capacity opening could take the ticket (the same `planOpening` the Open
 *    capacity panel runs — CORE-BO), the promise says so and points there; a failed check is said, not hidden.
 *
 * Read-only except the arrival decision, which goes through its server function (front office only, once per
 * arrival, audited). A failed read is said, never shown as "nothing needs attention".
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTechDayHours, getTechShiftBoundaryUtc, getTodayInShopTz } from '@/utils/shopTime';
import { clock } from '@/utils/clock';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from '@/hooks/use-toast';
import {
  ARRIVAL_DECISION_LABEL, describeArrivalHints, describeArrivalIdentity, describeArrivalRefusal,
  describeOpenTimer, describeRunLongAttribution, describeRunLongBaseline, hoursSince,
  describeRunLongConsequences, describeDueOutEpisode, type RunLongConsequence,
} from '@/lib/operations/attention';
import {
  capacityRecoveryPolicy, describePromiseOpening, OPEN_CAPACITY_TICKET_COLUMNS, openingsThatCouldTakeTicket, type CapacityOpeningRow,
} from '@/lib/capacity/openCapacity';

interface ArrivalRow {
  observation_id: string; observed_at: string; coverage_label: string | null; identity_state: string;
  hint_make: string | null; hint_model: string | null; hint_color: string | null; hint_body_style: string | null;
}
interface TimerFlagRow { id: string; ticket_id: string; technician_id: string; flagged_at: string; threshold_hours: number; started_at?: string }
interface RunLongRow {
  id: string; ticket_id: string; baseline_finish: string; baseline_basis: string; first_observed_at: string;
  observed_running_minutes: number | null; added_work_minutes: number | null; attribution: string;
}
interface LiveTicket { id: string; simple_ticket_number: number | null; customer_name: string | null; type?: string | null }
interface DueOutEpisodeRow { id: string; ticket_id: string; due_out: string; kind_at_detection: 'missed' | 'at_risk'; became_missed_at: string | null; max_projected_late_minutes: number | null }

type Decision = keyof typeof ARRIVAL_DECISION_LABEL;
// A member call, never a detached `supabase.rpc` (a detached method loses its client and throws before any request).
type LooseRpc = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }> };
const rpc = (fn: string, args: Record<string, unknown>) => (supabase as unknown as LooseRpc).rpc(fn, args);

export function NeedsAttentionPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  const shopId = activeShop?.id;
  const today = getTodayInShopTz(shopTimezone);
  const qc = useQueryClient();
  const [linkTo, setLinkTo] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['needs-attention', shopId, today],
    enabled: open && !!shopId,
    queryFn: async () => {
      const [arrivals, flags, runLong, live, techs, consequences, dueOut, openings, plannerTickets, culture] = await Promise.all([
        rpc('unexpected_arrivals', { p_shop_id: shopId, p_hours: 24 }),
        supabase.from('timer_session_flags' as never).select('id, ticket_id, technician_id, flagged_at, threshold_hours, session_id')
          .eq('shop_id', shopId!).is('resolved_at', null).order('flagged_at'),
        supabase.from('ticket_run_long_episodes' as never).select('id, ticket_id, baseline_finish, baseline_basis, first_observed_at, observed_running_minutes, added_work_minutes, attribution')
          .eq('shop_id', shopId!).is('ended_at', null).order('first_observed_at'),
        supabase.from('tickets').select('id, simple_ticket_number, customer_name, type').eq('shop_id', shopId!)
          .eq('scheduled_date', today).is('deleted_at', null).is('completed_at', null).is('picked_up_at', null).limit(300),
        supabase.from('technicians').select('id, name, hours').eq('shop_id', shopId!),
        rpc('run_long_consequences', { p_shop_id: shopId }),
        supabase.from('due_out_risk_episodes' as never).select('id, ticket_id, due_out, kind_at_detection, became_missed_at, max_projected_late_minutes')
          .eq('shop_id', shopId!).is('closed_at', null).order('due_out'),
        // R4 W5 (CORE-BH GAP-006): today's open openings and the planner's view of the tickets, for the pull-forward link.
        supabase.from('capacity_openings_current' as never).select('*').eq('shop_id', shopId!).eq('effective_status', 'open').eq('shop_day', today).order('window_start'),
        supabase.from('tickets').select(OPEN_CAPACITY_TICKET_COLUMNS).eq('shop_id', shopId!).is('deleted_at', null).gte('scheduled_date', today).limit(500),
        supabase.from('shop_profile').select('shop_culture').eq('shop_id', shopId!).maybeSingle(),
      ]);
      // The arrival list is front-office only: a refusal there is a permission fact, not a failure of the panel.
      const arrivalsForbidden = !!arrivals.error && /FRONT_OFFICE_ONLY/.test(arrivals.error.message);
      if (arrivals.error && !arrivalsForbidden) throw arrivals.error;
      for (const r of [flags, runLong, live, techs, consequences, dueOut]) if (r.error) throw r.error;
      const sessionIds = ((flags.data ?? []) as Array<{ session_id: string }>).map((f) => f.session_id);
      const sessions = sessionIds.length > 0
        ? await supabase.from('ticket_timer_sessions' as never).select('id, started_at').in('id', sessionIds)
        : { data: [], error: null };
      if (sessions.error) throw sessions.error;
      // Open timers and running-long visits can belong to visits not on today's list (overnight, rolled over):
      // label those too, rather than calling them "a visit".
      const todayIds = new Set(((live.data ?? []) as LiveTicket[]).map((t) => t.id));
      const otherIds = [...new Set([
        ...((flags.data ?? []) as Array<{ ticket_id: string }>).map((f) => f.ticket_id),
        ...((runLong.data ?? []) as Array<{ ticket_id: string }>).map((r) => r.ticket_id),
        ...((dueOut.data ?? []) as Array<{ ticket_id: string }>).map((r) => r.ticket_id),
        ...((consequences.data ?? []) as RunLongConsequence[]).flatMap((c) => [...c.late_starts.map((l) => l.ticket_id), ...c.promises.map((p) => p.ticket_id)]),
      ])].filter((id) => !todayIds.has(id));
      const others = otherIds.length > 0
        ? await supabase.from('tickets').select('id, simple_ticket_number, customer_name, type').in('id', otherIds)
        : { data: [], error: null };
      if (others.error) throw others.error;
      const startedBySession = new Map(((sessions.data ?? []) as Array<{ id: string; started_at: string }>).map((s) => [s.id, s.started_at]));
      return {
        arrivals: arrivalsForbidden ? null : ((arrivals.data ?? []) as ArrivalRow[]),
        flags: ((flags.data ?? []) as Array<TimerFlagRow & { session_id: string }>).map((f) => ({ ...f, started_at: startedBySession.get(f.session_id) })),
        runLong: (runLong.data ?? []) as unknown as RunLongRow[],
        live: (live.data ?? []) as LiveTicket[],
        labels: [...((live.data ?? []) as LiveTicket[]), ...((others.data ?? []) as LiveTicket[])],
        techNames: Object.fromEntries((techs.data ?? []).map((t) => [t.id, t.name])) as Record<string, string>,
        // The technician's end of day (canonical hours), to say when a run-long visit now crosses it.
        shiftEnds: Object.fromEntries((techs.data ?? []).map((t) => {
          const h = getTechDayHours(t as never, today);
          return [t.id, h ? getTechShiftBoundaryUtc(today, h.close, shopTimezone) : null];
        })) as Record<string, Date | null>,
        consequences: new Map(((consequences.data ?? []) as RunLongConsequence[]).map((c) => [c.episode_id, c])),
        dueOut: (dueOut.data ?? []) as unknown as DueOutEpisodeRow[],
        // null = the openings could not be checked (said on each promise), never "no opening".
        pullForward: openings.error || plannerTickets.error || culture.error ? null : {
          openings: (openings.data ?? []) as unknown as CapacityOpeningRow[],
          tickets: (plannerTickets.data ?? []) as never[],
          // oct20e (FD-2026-10-24-A; CORE-BO :74 as amended): permitted unless the shop restricts it
          allowFutureDay: capacityRecoveryPolicy(culture.data?.shop_culture).allowFutureDay,
        },
      };
    },
  });

  const ticketLabel = (id: string) => {
    const t = data?.labels.find((x) => x.id === id);
    return t ? `#${t.simple_ticket_number ?? '—'} ${t.customer_name ?? ''}`.trim() : 'a visit';
  };

  const decide = async (observationId: string, decision: Decision) => {
    const ticket = decision === 'linked_to_ticket' ? linkTo[observationId] : null;
    if (decision === 'linked_to_ticket' && !ticket) {
      toast({ title: 'Choose the visit first', description: 'Pick the open visit this arrival belongs to.' });
      return;
    }
    setBusy(observationId);
    const { error } = await rpc('decide_arrival', { p_observation_id: observationId, p_decision: decision, p_ticket_id: ticket });
    setBusy(null);
    if (error) {
      toast({ title: 'Not recorded', description: describeArrivalRefusal(error.message), variant: 'destructive' });
      return;
    }
    toast({ title: 'Recorded', description: 'The vehicle was not moved — place it from the board if needed.' });
    void qc.invalidateQueries({ queryKey: ['needs-attention', shopId, today] });
  };

  const nowMs = clock.nowMs();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="needs-attention-panel">
        <SheetHeader>
          <SheetTitle>Needs attention</SheetTitle>
          <SheetDescription>Things the shop's records say a person should look at. Nothing here acts on its own.</SheetDescription>
        </SheetHeader>
        {isLoading && <p className="mt-4 text-sm text-muted-foreground">Loading…</p>}
        {isError && <p className="mt-4 text-sm text-destructive" data-testid="needs-attention-error">This could not be loaded — it is not a sign that nothing needs attention.</p>}
        {data && (
          <div className="mt-4 space-y-6">
            <section aria-label="Unexpected arrivals">
              <h3 className="mb-2 text-sm font-semibold">Unexpected arrivals <Badge variant="secondary">{data.arrivals?.length ?? '—'}</Badge></h3>
              {data.arrivals === null && <p className="text-sm text-muted-foreground">The front office reviews arrivals.</p>}
              {data.arrivals?.length === 0 && <p className="text-sm text-muted-foreground">No unmatched arrival in the last 24 hours.</p>}
              {data.arrivals?.map((a) => (
                <div key={a.observation_id} className="mb-3 space-y-2 rounded-md border p-3" data-testid="unexpected-arrival">
                  <p className="text-sm">{describeArrivalHints(a)} · {new Date(a.observed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: shopTimezone })}{a.coverage_label ? ` · ${a.coverage_label}` : ''}</p>
                  <p className="text-xs text-muted-foreground">{describeArrivalIdentity(a.identity_state)}</p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <select
                      aria-label="Open visit"
                      className="h-11 rounded-md border bg-background px-2 text-sm"
                      value={linkTo[a.observation_id] ?? ''}
                      onChange={(e) => setLinkTo((m) => ({ ...m, [a.observation_id]: e.target.value }))}
                    >
                      <option value="">Choose the visit…</option>
                      {data.live.map((t) => <option key={t.id} value={t.id}>{ticketLabel(t.id)}</option>)}
                    </select>
                    <Button size="sm" disabled={busy === a.observation_id} onClick={() => void decide(a.observation_id, 'linked_to_ticket')}>{ARRIVAL_DECISION_LABEL.linked_to_ticket}</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" disabled={busy === a.observation_id} onClick={() => void decide(a.observation_id, 'not_a_service_visit')}>{ARRIVAL_DECISION_LABEL.not_a_service_visit}</Button>
                    <Button size="sm" variant="outline" disabled={busy === a.observation_id} onClick={() => void decide(a.observation_id, 'could_not_identify')}>{ARRIVAL_DECISION_LABEL.could_not_identify}</Button>
                  </div>
                </div>
              ))}
            </section>

            <section aria-label="Timers open too long">
              <h3 className="mb-2 text-sm font-semibold">Timers open too long <Badge variant="secondary">{data.flags.length}</Badge></h3>
              {data.flags.length === 0 && <p className="text-sm text-muted-foreground">No timer has been running longer than this shop's check.</p>}
              {data.flags.map((f) => (
                <div key={f.id} className="mb-2 rounded-md border p-3 text-sm" data-testid="open-timer-flag">
                  <p className="font-medium">{data.techNames[f.technician_id] ?? 'A technician'} · {ticketLabel(f.ticket_id)}</p>
                  <p className="text-xs text-muted-foreground">{describeOpenTimer(f.started_at ? hoursSince(f.started_at, nowMs) : f.threshold_hours, f.threshold_hours)}</p>
                </div>
              ))}
            </section>

            <section aria-label="Running long">
              <h3 className="mb-2 text-sm font-semibold">Running long <Badge variant="secondary">{data.runLong.length}</Badge></h3>
              {data.runLong.length === 0 && <p className="text-sm text-muted-foreground">No visit is running past its expected finish.</p>}
              {data.runLong.map((r) => (
                <div key={r.id} className="mb-2 rounded-md border p-3 text-sm" data-testid="run-long-episode">
                  <p className="font-medium">{ticketLabel(r.ticket_id)}</p>
                  <p className="text-xs">{describeRunLongAttribution(r.attribution)}{r.added_work_minutes ? ` (${r.added_work_minutes} min added)` : ''}</p>
                  <p className="text-xs text-muted-foreground">Measured {describeRunLongBaseline(r.baseline_basis)}.</p>
                  {(() => {
                    const c = data.consequences.get(r.id);
                    const lines = describeRunLongConsequences(c, ticketLabel, c?.technician_id ? data.shiftEnds[c.technician_id] ?? null : null);
                    return lines.length > 0 ? (
                      <ul className="mt-1 list-disc pl-4 text-xs" data-testid="run-long-consequences">
                        {lines.map((l) => <li key={l}>{l}</li>)}
                      </ul>
                    ) : <p className="mt-1 text-xs text-muted-foreground" data-testid="run-long-consequences">Nothing else on the board is affected yet.</p>;
                  })()}
                </div>
              ))}
            </section>

            <section aria-label="Promises in trouble">
              <h3 className="mb-2 text-sm font-semibold">Promises in trouble <Badge variant="secondary">{data.dueOut.length}</Badge></h3>
              {data.dueOut.length === 0 && <p className="text-sm text-muted-foreground">No promise is at risk or missed right now.</p>}
              {[...data.dueOut]
                .map((e) => ({ ...e, kind: (e.became_missed_at || new Date(e.due_out).getTime() < nowMs ? 'missed' : 'at_risk') as 'missed' | 'at_risk' }))
                .sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'missed' ? -1 : 1)
                  || ((data.labels.find((t) => t.id === a.ticket_id)?.type === 'Waiter' ? 0 : 1) - (data.labels.find((t) => t.id === b.ticket_id)?.type === 'Waiter' ? 0 : 1))
                  || (new Date(a.due_out).getTime() - new Date(b.due_out).getTime()))
                .map((e) => (
                  <div key={e.id} className="mb-2 rounded-md border p-3 text-sm" data-testid="due-out-episode" data-kind={e.kind}>
                    <p className="font-medium">{ticketLabel(e.ticket_id)}{data.labels.find((t) => t.id === e.ticket_id)?.type === 'Waiter' ? ' · customer waiting' : ''}</p>
                    <p className="text-xs">{describeDueOutEpisode(e, shopTimezone)}</p>
                    {data.pullForward === null
                      ? <p className="mt-1 text-xs text-muted-foreground" data-testid="due-out-pull-forward">Open capacity could not be checked for this promise.</p>
                      : openingsThatCouldTakeTicket(e.ticket_id, e.due_out, data.pullForward.openings, data.pullForward.tickets, { now: new Date(nowMs), allowFutureDay: data.pullForward.allowFutureDay })
                          .map((m) => (
                            <p key={m.opening.id} className="mt-1 text-xs" data-testid="due-out-pull-forward">
                              {describePromiseOpening(m, (m.opening.technician_id && data.techNames[m.opening.technician_id]) || null, shopTimezone)}
                            </p>
                          ))}
                  </div>
                ))}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
